/**
 * First-run starter team (task #1525, server side #1524, docs/specs/starter-team.md).
 * Pure helpers: the card only renders what these return.
 */

/** GET /api/onboarding. `connect` is null unless a machine still has to be connected. */
export type OnboardingStatus = {
  needsStarterTeam: boolean;
  agentCount: number;
  nodeCount: number;
  onlineNodeCount: number;
  needsMachine: boolean;
  connect: { hub: string; steps: string[] } | null;
};

/** POST /api/onboarding/starter-team: the new agents and the status after them. */
export type StarterTeamResult = { agents: { id: string; name: string }[]; onboarding: OnboardingStatus };

/** The three seats the server creates, before a click. The server may suffix a name (builder-2); the result says which. */
export const STARTER_TEAM_SEATS: { name: string; role: string }[] = [
  { name: "spec-agent", role: "Turns an idea into a spec and tasks" },
  { name: "builder", role: "Builds the UI and core changes" },
  { name: "reviewer", role: "Reviews finished work" },
];

/** Set in this browser when the person creates the starter team, so the connect steps survive a reload. */
export const STARTER_TEAM_FLAG = "roomate:starter-team-created";

/** Whether this browser created the starter team. Blocked site storage (SecurityError) reads as not created. */
export function readStarterTeamFlag(): boolean {
  try {
    return localStorage.getItem(STARTER_TEAM_FLAG) === "1";
  } catch {
    return false;
  }
}

/**
 * A machine runs the agents: the team exists, at least one agent is on the office, and no machine is still needed.
 * A stale status still offering the team (needsStarterTeam) never counts, so the connect steps are not cleared early.
 */
export function machineRunsAgents(status: OnboardingStatus | null): boolean {
  return status !== null && !status.needsStarterTeam && status.agentCount > 0 && !status.needsMachine;
}

/** Forget the creation once a machine runs the agents: the steps are done, and the flag must not revive them later. */
export function clearStarterTeamFlag(): void {
  try {
    localStorage.removeItem(STARTER_TEAM_FLAG);
  } catch {
    // Storage can be off: nothing was stored, so nothing to clear.
  }
}

/**
 * Which first-run block to show, or null for none.
 *  - Offer: the office has no agent (only an admin can create the team).
 *  - Connect: only for the person who just created the team in this browser, while no member machine runs
 *    the agents yet, and only for an admin (the steps mint tokens). An office set up before this feature
 *    never sees it: the server's `needsMachine` alone is true for any agent off an online node.
 */
export function onboardingView(status: OnboardingStatus | null, opts: { admin: boolean; startedHere: boolean }): "offer" | "connect" | null {
  if (!status || !opts.admin) return null;
  if (status.needsStarterTeam) return "offer";
  if (opts.startedHere && status.needsMachine && status.connect) return "connect";
  return null;
}

/** The server's connect steps name the hub as a placeholder; the web app knows the real origin. */
export function connectSteps(connect: { hub: string; steps: string[] }, origin: string): string[] {
  if (!connect.hub) return connect.steps;
  return connect.steps.map((step) => step.split(connect.hub).join(origin));
}

/** True when the address only reaches this machine: another machine on the network cannot use the commands. */
export function isLoopbackOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host === "::1" || host.endsWith(".localhost");
  } catch {
    return false;
  }
}
